import { useState, useEffect, useMemo } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Plug, Loader2, Copy, CheckCircle2, AlertTriangle, ChevronDown, ChevronUp, GitCompare } from "lucide-react";

const V2_STATE_STYLES = {
  validated: "bg-green-50 border-green-200 text-green-800",
  installed_test_required: "bg-amber-50 border-amber-200 text-amber-800",
  update_required: "bg-orange-50 border-orange-200 text-orange-800",
  to_install: "bg-slate-50 border-slate-200 text-slate-700",
};

export function VeloBridgePanel({ siteId }) {
  const [status, setStatus] = useState(null);
  const [testing, setTesting] = useState(false);
  const [info, setInfo] = useState(null);
  const [open, setOpen] = useState(false);
  const [view, setView] = useState("v2");
  const [marking, setMarking] = useState(false);
  const [markedNote, setMarkedNote] = useState(null);

  useEffect(() => {
    if (!siteId) return;
    api.get(`/sites/${siteId}/velo/snippet`).then(({ data }) => setInfo(data)).catch(() => {});
  }, [siteId]);

  const test = async () => {
    setTesting(true);
    try {
      const { data } = await api.get(`/sites/${siteId}/velo/status`);
      setStatus(data);
      if (data.state === "validated") toast.success("Pont Velo validé ✓");
      else toast.warning(data.message);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Test impossible");
    } finally { setTesting(false); }
  };

  const markInstalled = async () => {
    setMarking(true);
    try {
      const { data } = await api.post(`/sites/${siteId}/velo/mark-v2-installed`);
      setMarkedNote(data.note);
      toast.info(data.note);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Action impossible");
    } finally { setMarking(false); }
  };

  // Diff réel généré à partir des deux versions exactes du snippet (v1 / v2)
  const diff = useMemo(() => {
    if (!info?.snippet_v1 || !info?.snippet) return null;
    const v1 = info.snippet_v1.split("\n");
    const v2 = info.snippet.split("\n");
    const v1set = new Set(v1);
    const v2set = new Set(v2);
    return {
      removed: v1.filter((l) => !v2set.has(l)),
      annotated: v2.map((l) => ({ added: !v1set.has(l), l })),
    };
  }, [info]);

  return (
    <div className="mt-6 border border-slate-200 bg-white rounded-md overflow-hidden" data-testid="velo-bridge-panel">
      <div className="px-5 py-3 border-b border-slate-200 bg-slate-50 flex items-center justify-between">
        <div className="overline flex items-center gap-2"><Plug className="w-3.5 h-3.5" /> Pont Velo — pages statiques Wix</div>
        <button onClick={test} disabled={testing} data-testid="velo-status-button"
          className="inline-flex items-center gap-1.5 text-xs font-medium text-[#002FA7] border border-[#002FA7]/30 rounded-md px-3 py-1.5 hover:bg-blue-50">
          {testing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Plug className="w-3.5 h-3.5" />} Tester le pont Velo
        </button>
      </div>
      <div className="p-5">
        <p className="text-sm text-slate-600">
          Les pages statiques Wix ne sont pas modifiables par l'API REST. Le pont Velo contourne cette limite :
          l'app écrit les corrections dans une collection CMS Wix (« SeoOverrides ») et un petit script
          Velo installé sur votre site les applique au rendu de chaque page — y compris dans le HTML vu par Google (SSR).
        </p>

        <div className="mt-4 border border-indigo-200 bg-indigo-50 rounded-md p-3" data-testid="velo-v2-banner">
          <div className="text-sm font-semibold text-indigo-900">Script Velo v2 disponible — Canonical · Open Graph · Schema (Phase 4B)</div>
          <div className="text-xs text-indigo-800 mt-1">
            Version requise par l'application : <strong>v2</strong>. La mise à jour v1 → v2 conserve tous les overrides
            title/meta existants (rien n'est supprimé). Après remplacement du script dans <code className="font-mono">masterPage.js</code>,
            <strong> publiez le site Wix</strong>.
          </div>
          {status?.snippet_v2 && (
            <div className={`mt-2 inline-block text-xs font-medium border rounded px-2 py-1 ${V2_STATE_STYLES[status.snippet_v2.status] || V2_STATE_STYLES.to_install}`} data-testid="velo-v2-state">
              {status.snippet_v2.label}
              {status.snippet_v2.status === "validated" && status.snippet_v2.validated_at && (
                <span className="font-normal"> · {new Date(status.snippet_v2.validated_at).toLocaleString("fr-FR")}</span>
              )}
            </div>
          )}
          <div className="mt-2 flex flex-wrap items-center gap-2">
            <button onClick={() => { setOpen(true); setView("v2"); }} data-testid="velo-v2-view-button"
              className="text-xs font-medium text-indigo-700 border border-indigo-300 rounded-md px-2.5 py-1 hover:bg-indigo-100">
              Voir le script v2 complet
            </button>
            <button onClick={() => { setOpen(true); setView("diff"); }} data-testid="velo-v2-diff-button"
              className="inline-flex items-center gap-1 text-xs font-medium text-indigo-700 border border-indigo-300 rounded-md px-2.5 py-1 hover:bg-indigo-100">
              <GitCompare className="w-3 h-3" /> Diff v1 → v2
            </button>
            {info && (
              <button onClick={() => { navigator.clipboard.writeText(info.snippet); toast.success("Script v2 copié !"); }}
                data-testid="velo-v2-copy-button"
                className="inline-flex items-center gap-1 text-xs font-medium text-indigo-700 border border-indigo-300 rounded-md px-2.5 py-1 hover:bg-indigo-100">
                <Copy className="w-3 h-3" /> Copier le script v2
              </button>
            )}
            <button onClick={markInstalled} disabled={marking} data-testid="velo-v2-mark-installed-button"
              className="text-xs font-medium text-slate-600 border border-slate-300 rounded-md px-2.5 py-1 hover:bg-slate-100">
              {marking ? "…" : "Marquer v2 comme installé"}
            </button>
          </div>
          <div className="mt-1.5 text-[11px] text-indigo-700">
            « Marquer comme installé » est une étape intermédiaire déclarative — <strong>jamais une preuve</strong>.
            Le statut « v2 validé ✓ » n'apparaît qu'après une correction Canonical/OG/Schema réellement retrouvée dans le HTML public.
          </div>
          {markedNote && <div className="mt-1.5 text-[11px] text-amber-800" data-testid="velo-v2-marked-note">{markedNote}</div>}
        </div>

        {status && (() => {
          const STATES = {
            validated: { label: "Pont Velo validé ✓", cls: "bg-green-50 border-green-200 text-green-800", ok: true },
            revalidation_required: { label: "Validation à reconfirmer", cls: "bg-orange-50 border-orange-200 text-orange-800", ok: false },
            public_test_required: { label: "Test public requis", cls: "bg-amber-50 border-amber-200 text-amber-800", ok: false },
            wixdata_connected: { label: "Wix Data connecté — script Velo à installer", cls: "bg-blue-50 border-blue-200 text-blue-800", ok: false },
            not_installed: { label: "Non installé", cls: "bg-slate-50 border-slate-200 text-slate-700", ok: false },
          };
          const st = STATES[status.state] || STATES.not_installed;
          return (
            <div className={`mt-3 text-sm rounded-md px-3 py-2 border ${st.cls}`} data-testid="velo-status-result">
              <div className="flex items-start gap-2">
                {st.ok ? <CheckCircle2 className="w-4 h-4 mt-0.5 flex-shrink-0" /> : <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />}
                <div>
                  <div className="font-semibold" data-testid="velo-state-label">{st.label}</div>
                  <div className="mt-0.5">{status.message}</div>
                  {status.state === "validated" && status.validated_at && (
                    <div className="mt-1 text-xs" data-testid="velo-validation-meta">
                      Dernière preuve publique : {new Date(status.validated_at).toLocaleString("fr-FR")} · {status.validated_page_url} · balise {status.validated_category}
                    </div>
                  )}
                </div>
              </div>
            </div>
          );
        })()}

        <button onClick={() => setOpen(!open)} data-testid="velo-instructions-toggle"
          className="mt-4 inline-flex items-center gap-1 text-sm font-medium text-[#002FA7] hover:underline">
          {open ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          Instructions d'installation / migration
        </button>
        {open && info && (
          <div className="mt-3">
            <ol className="list-decimal ml-5 space-y-2 text-sm text-slate-700">
              {info.steps.map((s, i) => <li key={i}>{s}</li>)}
            </ol>
            <div className="mt-4 flex items-center gap-3 border-b border-slate-200">
              <button onClick={() => setView("v2")} data-testid="velo-tab-v2"
                className={`text-xs font-medium pb-1.5 border-b-2 ${view === "v2" ? "border-[#002FA7] text-slate-900" : "border-transparent text-slate-500"}`}>
                Script v2 complet
              </button>
              <button onClick={() => setView("diff")} data-testid="velo-tab-diff"
                className={`text-xs font-medium pb-1.5 border-b-2 ${view === "diff" ? "border-[#002FA7] text-slate-900" : "border-transparent text-slate-500"}`}>
                Diff v1 → v2
              </button>
              <div className="flex-1" />
              <button onClick={() => { navigator.clipboard.writeText(info.snippet); toast.success("Script v2 copié !"); }}
                data-testid="velo-copy-snippet" className="inline-flex items-center gap-1 text-xs text-[#002FA7] hover:underline pb-1.5">
                <Copy className="w-3 h-3" /> Copier v2
              </button>
            </div>
            {view === "v2" ? (
              <pre className="mt-2 bg-slate-950 text-slate-100 text-xs rounded-md p-4 overflow-x-auto whitespace-pre" data-testid="velo-snippet-code">{info.snippet}</pre>
            ) : (
              <div className="mt-2" data-testid="velo-diff-view">
                {diff?.removed.length > 0 && (
                  <>
                    <div className="text-[11px] font-semibold text-red-600 uppercase tracking-wider mb-1">Supprimé (v1)</div>
                    <pre className="bg-slate-950 text-xs rounded-md p-3 overflow-x-auto whitespace-pre mb-2">
                      {diff.removed.map((l, i) => <div key={i} className="text-red-400">- {l}</div>)}
                    </pre>
                  </>
                )}
                <div className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider mb-1">Script v2 (ajouts en vert — conserve title/description/normalisation URL/enabled)</div>
                <pre className="bg-slate-950 text-xs rounded-md p-3 overflow-x-auto whitespace-pre">
                  {diff?.annotated.map((r, i) => (
                    <div key={i} className={r.added ? "text-green-400" : "text-slate-300"}>{r.added ? "+ " : "  "}{r.l}</div>
                  ))}
                </pre>
              </div>
            )}
            <p className="mt-3 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-3 py-2">
              ⚠️ Test réel obligatoire : après installation, appliquez une correction sur une page statique puis utilisez
              « Vérifier » — le statut « Vérifié ✓ » ne s'affiche que si la balise est réellement présente dans le HTML
              public de la page (testez le site PUBLIÉ, pas le mode Preview Wix).
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
